// Pure aggregation helpers for the Calls page (VoIP Reports) over rhinoapi's
// GET /analytics/calls/breakdown rows: pre-aggregated counts from both call
// systems (rhinoVoip and RhinoCall) per local day / hour / weekday x source x
// direction x outcome x member x extension x ring group, with count,
// talkSecSum and ringSecSum. Everything on the page derives from these rows.
import moment from 'moment-timezone';
import { daysInRange, periodKey, daysPerPeriod, shortDay, sortRows } from './MessagingReportsHelpers';

export { daysInRange, periodKey, daysPerPeriod, shortDay, sortRows };

export const CALL_SOURCES = [
  { id: 'all', label: 'All Call Systems' },
  { id: 'rhinovoip', label: 'RhinoVoip (phone system)' },
  { id: 'rhinocall', label: 'RhinoCall (in-app)' },
];
export const sourceLabel = (id) => CALL_SOURCES.find((s) => s.id === id)?.label || id;

export const OUTCOME_LABELS = {
  answered: 'Answered', missed: 'Missed', voicemail: 'Voicemail', no_answer: 'No Answer', busy: 'Busy', failed: 'Failed',
};
export const outcomeLabel = (o) => OUTCOME_LABELS[o] || o;

// Voicemail rolls into missed: a voicemail is a missed call where the caller
// chose to leave a message (same rule as the previous page).
const isMissed = (o) => o === 'missed' || o === 'no_answer' || o === 'voicemail';
const isFailed = (o) => o === 'failed' || o === 'busy';

// Member identity for a row: the rhinoapi member when the extension mapped to one,
// otherwise the bare extension. RhinoCall rows always carry a member.
export const channelKeyOf = (row) => (row.channelId ? row.channelId : null);

export function memberKeyOf(row) {
  if (row.memberId) return `m${row.memberId}`;
  if (row.extension) return `x${row.extension}`;
  return null;
}

export function filterRows(rows, { source = 'all', memberKey = null, ringGroup = null, channelId = null } = {}) {
  return rows.filter((row) => {
    if (source !== 'all' && row.source !== source) return false;
    if (memberKey !== null && memberKeyOf(row) !== memberKey) return false;
    if (channelId !== null && row.channelId !== channelId) return false;
    if (ringGroup !== null && row.ringGroup !== ringGroup) return false;
    return true;
  });
}

// answered/missed/voicemail are inbound-only (the phone system view); answeredAll,
// voicemailAll and noAnswer count every direction (the RhinoCall view, where every
// call is outbound).
export const emptyTotals = () => ({
  total: 0, inbound: 0, outbound: 0, answered: 0, missed: 0, voicemail: 0, failed: 0, answeredAll: 0, voicemailAll: 0, noAnswer: 0, talkSecSum: 0, ringSecSum: 0,
});

export function addRow(totals, row) {
  const t = totals;
  const n = row.count;
  t.total += n;
  if (row.direction === 'inbound') {
    t.inbound += n;
    if (row.outcome === 'answered') t.answered += n;
    if (isMissed(row.outcome)) t.missed += n;
    if (row.outcome === 'voicemail') t.voicemail += n;
  } else {
    t.outbound += n;
  }
  if (isFailed(row.outcome)) t.failed += n;
  if (row.outcome === 'voicemail') t.voicemailAll += n;
  if (row.outcome === 'missed' || row.outcome === 'no_answer') t.noAnswer += n;
  if (row.outcome === 'answered') {
    t.answeredAll += n;
    t.talkSecSum += row.talkSecSum || 0;
    t.ringSecSum += row.ringSecSum || 0;
  }
  return t;
}

export const sumRows = (rows) => rows.reduce(addRow, emptyTotals());

export const finishTotals = (t, dayCount) => ({
  ...t,
  avgPerDay: dayCount ? Math.round((t.total / dayCount) * 10) / 10 : 0,
  // % Missed is missed / inbound: outbound calls can't be missed.
  pctMissed: t.inbound > 0 ? Math.round((t.missed / t.inbound) * 100) : 0,
  pctAnswered: t.total > 0 ? Math.round((t.answeredAll / t.total) * 100) : 0,
  avgHandleTime: t.answeredAll > 0 ? Math.round(t.talkSecSum / t.answeredAll) : 0,
  avgAnswerSecs: t.answeredAll > 0 ? Math.round(t.ringSecSum / t.answeredAll) : 0,
});

export function formatHandleTime(seconds) {
  if (!seconds) return '00:00';
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

export function summaryByPeriod(rows, days, grouping) {
  const perPeriodDays = daysPerPeriod(days, grouping);
  const buckets = {};
  Object.keys(perPeriodDays).forEach((k) => { buckets[k] = emptyTotals(); });
  rows.forEach((row) => {
    const key = periodKey(row.day, grouping);
    if (!buckets[key]) buckets[key] = emptyTotals();
    addRow(buckets[key], row);
  });
  return Object.keys(buckets).map((period) => ({ period, ...finishTotals(buckets[period], perPeriodDays[period] || 0) }));
}

// Calls by outcome (donut); inbound only unless allDirections.
export function inboundByOutcome(rows, allDirections = false) {
  const counts = {};
  rows.forEach((r) => { if (allDirections || r.direction === 'inbound') counts[r.outcome] = (counts[r.outcome] || 0) + r.count; });
  return Object.entries(counts).map(([o, value]) => ({ name: outcomeLabel(o), value })).sort((a, b) => b.value - a.value);
}

export function byDirection(rows) {
  const t = sumRows(rows);
  return [{ name: 'Inbound', value: t.inbound }, { name: 'Outbound', value: t.outbound }].filter((s) => s.value > 0);
}

export function bySource(rows) {
  const counts = {};
  rows.forEach((r) => { counts[r.source] = (counts[r.source] || 0) + r.count; });
  return Object.entries(counts).map(([s, value]) => ({ name: sourceLabel(s), value })).sort((a, b) => b.value - a.value);
}

export function perHourSeries(byHourRows, totalDays) {
  const hours = Array.from({ length: 24 }, (_, h) => ({ hour: moment(h, 'H').format('h a'), inbound: 0, outbound: 0 }));
  byHourRows.forEach((r) => { if (r.hour >= 0 && r.hour < 24) hours[r.hour][r.direction === 'inbound' ? 'inbound' : 'outbound'] += r.count; });
  const d = Math.max(totalDays, 1);
  return hours.map((h) => ({
    ...h, inbound: Math.round((h.inbound / d) * 10) / 10, outbound: Math.round((h.outbound / d) * 10) / 10, total: Math.round(((h.inbound + h.outbound) / d) * 10) / 10,
  }));
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export function perWeekdaySeries(byWeekdayRows, days) {
  const occurrences = Array(7).fill(0);
  days.forEach((day) => { occurrences[moment(day, 'YYYY-MM-DD').isoWeekday() - 1] += 1; });
  const totals = WEEKDAYS.map((day) => ({ day, inbound: 0, outbound: 0 }));
  byWeekdayRows.forEach((r) => { if (r.weekday >= 0 && r.weekday < 7) totals[r.weekday][r.direction === 'inbound' ? 'inbound' : 'outbound'] += r.count; });
  return totals.map((t, i) => {
    const d = Math.max(occurrences[i], 1);
    return {
      ...t, inbound: Math.round((t.inbound / d) * 10) / 10, outbound: Math.round((t.outbound / d) * 10) / 10, total: Math.round(((t.inbound + t.outbound) / d) * 10) / 10,
    };
  });
}

export function perDaySeries(rows, days) {
  const byDay = {};
  days.forEach((day) => { byDay[day] = { date: shortDay(day), day, inbound: 0, outbound: 0 }; });
  rows.forEach((r) => { if (byDay[r.day]) byDay[r.day][r.direction === 'inbound' ? 'inbound' : 'outbound'] += r.count; });
  return days.map((day) => ({ ...byDay[day], total: byDay[day].inbound + byDay[day].outbound }));
}

// Per category (member or ring group) totals, ranked; `seed` lists every category
// that should appear even with zero calls.
export function totalsByCategory(rows, keyOf, nameOf, dayCount, seed = []) {
  const buckets = {};
  seed.forEach((s) => { buckets[s.key] = { key: s.key, name: s.name, extension: s.extension || '', ...emptyTotals() }; });
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === null || key === undefined || key === '') return;
    if (!buckets[key]) buckets[key] = { key, name: nameOf(key, row), extension: row.extension || '', ...emptyTotals() };
    addRow(buckets[key], row);
  });
  return Object.values(buckets).map((b) => finishTotals(b, dayCount)).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

export function categoryByPeriod(rows, keyOf, nameOf, days, grouping, seed = []) {
  if (grouping === 'total') return totalsByCategory(rows, keyOf, nameOf, days.length, seed);
  const perPeriodDays = daysPerPeriod(days, grouping);
  const buckets = {};
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === null || key === undefined || key === '') return;
    const period = periodKey(row.day, grouping);
    const id = `${key}|${period}`;
    if (!buckets[id]) buckets[id] = { key, name: nameOf(key, row), extension: row.extension || '', period, ...emptyTotals() };
    addRow(buckets[id], row);
  });
  const periods = [...new Set(Object.values(buckets).map((b) => b.period))];
  seed.forEach((s) => periods.forEach((period) => {
    const id = `${s.key}|${period}`;
    if (!buckets[id]) buckets[id] = { key: s.key, name: s.name, extension: s.extension || '', period, ...emptyTotals() };
  }));
  return Object.values(buckets).map((b) => finishTotals(b, perPeriodDays[b.period] || 0))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

export function dailyByCategory(rows, keyOf, days, maxSeries = 10) {
  const totals = {};
  rows.forEach((row) => { const k = keyOf(row); if (k) totals[k] = (totals[k] || 0) + row.count; });
  const keys = Object.keys(totals).sort((a, b) => totals[b] - totals[a]).slice(0, maxSeries);
  const byDay = {};
  days.forEach((day) => { byDay[day] = { date: shortDay(day), day }; keys.forEach((k) => { byDay[day][k] = 0; }); });
  rows.forEach((row) => { const k = keyOf(row); if (k && byDay[row.day] && keys.includes(k)) byDay[row.day][k] += row.count; });
  return { data: days.map((day) => byDay[day]), keys };
}
